import { Box, Button, Flex, HStack, Heading, Icon, Text, VStack, useToast } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiEye, FiSettings, FiShield, FiInfo, FiLogIn, FiUserPlus, FiUserX } from "react-icons/fi";
import React, { useEffect, useMemo, useState } from "react";
import { withModel, bindToCommand, isLoggedIn, VOTING_STATUSES } from "@lubbezposrednio-frontend/core";
import { VotingViewModel } from "./VotingViewModel";
import { VotingInfoTile } from "./components/VotingInfo/VotingInfoTile";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { VotingViewQuestion } from "./components/VotingViewQuestion/VotingViewQuestion";
import { VotingViewEmail } from "./components/VotingViewEmail/VotingViewEmail";
import { VotingPreviewBox } from "../LandingPageView/components/VotingPreviewBox";
import { WalletHandoff } from "./components/WalletHandoff";
import { CustomButton, CustomLoader } from "@lubbezposrednio-frontend/commons";
import { AdaptiveShell } from "../OrganizerDashboardView/components/AdaptiveShell";
import ReportPopup from "./components/ReportPopup/ReportPopup";
import { VotingPlannedView } from "./components/VotingPlannedView";
import { OnChainVotes } from "./components/OnChainVotes";
import { BlockchainInfo } from "./components/BlockchainInfo";
import { VotingFinishedScreen } from "../VotingFinishedScreen/VotingFinishedScreen";
import { CyberidVerifyGate } from "./components/CyberidVerifyGate";

export const VOTE_TILE_WIDTH = 813;

// Pasek akcji widoczny tylko dla właściciela głosowania (podgląd publicznego linku).
const OrganizerToolbar: React.FC<{ id: string }> = ({ id }) => (
  <Box w="100%" maxW="720px" mx="auto" px={{ base: "16px", md: "20px" }} pt={{ base: "20px", md: "28px" }}>
    <Flex bg="rgba(67,0,152,0.05)" border="1px solid rgba(67,0,152,0.16)" borderRadius="14px" p="12px 16px" align="center" justify="space-between" gap="12px" wrap="wrap">
      <HStack spacing="10px">
        <Icon as={FiEye} color="primary" boxSize="18px" />
        <Box>
          <Text fontWeight="800" color="primary" fontSize="14px">
            Podgląd jako organizator
          </Text>
          <Text fontSize="12px" color="gray.500">
            Tak tę stronę widzą głosujący.
          </Text>
        </Box>
      </HStack>
      <Button as={Link} to={`/organizator/glosowanie/${id}`} size="sm" bg="primary" color="white" borderRadius="10px" fontWeight="700" leftIcon={<FiSettings />} _hover={{ bg: "#36007a" }}>
        Zarządzaj głosowaniem
      </Button>
    </Flex>
  </Box>
);

// Bramka dla głosowań wymagających weryfikacji tożsamości — zamiast „gołego" inputu e-mail.
const VerificationGate: React.FC<{ loggedIn: boolean }> = ({ loggedIn }) => (
  <Flex direction="column" align="center" textAlign="center" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.10)" p={{ base: "34px 22px", md: "44px 36px" }}>
    <Flex w="64px" h="64px" borderRadius="full" bg="rgba(67,0,152,0.08)" align="center" justify="center" mb="16px">
      <Icon as={FiShield} boxSize="30px" color="primary" />
    </Flex>
    <HStack spacing="6px" bg="rgba(67,0,152,0.06)" color="primary" px="11px" py="4px" borderRadius="full" mb="12px">
      <Icon as={FiShield} boxSize="13px" />
      <Text fontSize="12px" fontWeight="700">
        Publiczne · tylko dla zweryfikowanych
      </Text>
    </HStack>
    <Heading fontSize="22px" color="primary" fontWeight="800" mb="10px">
      To głosowanie wymaga zweryfikowanej tożsamości
    </Heading>
    <Text color="gray.600" mb="10px" lineHeight="1.6" maxW="460px">
      Jest publicznie widoczne, ale głosować mogą w nim wyłącznie osoby z potwierdzoną tożsamością (mObywatel) — dzięki temu każdy głos pochodzi od uprawnionej, realnej osoby, a wynik jest wiarygodny.
    </Text>
    <Text color="gray.500" fontSize="14px" mb="22px" lineHeight="1.6" maxW="460px">
      {loggedIn
        ? "Twoje konto nie jest jeszcze zweryfikowane. Zweryfikuj tożsamość, aby oddać głos."
        : "Zaloguj się, a następnie zweryfikuj tożsamość, aby oddać głos."}
    </Text>
    {loggedIn ? (
      <Button as={Link} to="/organizator/konto" bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiShield />} _hover={{ bg: "#36007a" }}>
        Zweryfikuj konto
      </Button>
    ) : (
      <Button as={Link} to="/logowanie" bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiLogIn />} _hover={{ bg: "#36007a" }}>
        Zaloguj się
      </Button>
    )}
  </Flex>
);

// Bramka „tylko z kontami": głosować mogą wyłącznie zalogowane konta — niezalogowany dostaje wezwanie do logowania
// (zamiast formularza e-mail, który jest dla anonimowych). Egzekwowane też po stronie serwera (Votes::VotingPolicy).
const AccountRequiredGate: React.FC = () => (
  <Flex direction="column" align="center" textAlign="center" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.10)" p={{ base: "34px 22px", md: "44px 36px" }}>
    <Flex w="64px" h="64px" borderRadius="full" bg="rgba(67,0,152,0.08)" align="center" justify="center" mb="16px">
      <Icon as={FiUserPlus} boxSize="30px" color="primary" />
    </Flex>
    <HStack spacing="6px" bg="rgba(67,0,152,0.06)" color="primary" px="11px" py="4px" borderRadius="full" mb="12px">
      <Icon as={FiUserPlus} boxSize="13px" />
      <Text fontSize="12px" fontWeight="700">Tylko z kontami</Text>
    </HStack>
    <Heading fontSize="22px" color="primary" fontWeight="800" mb="10px">
      To głosowanie wymaga konta
    </Heading>
    <Text color="gray.600" mb="8px" lineHeight="1.6" maxW="460px">
      Głosować mogą wyłącznie osoby z kontem. <b>Załóż konto na adres, na który otrzymałeś zaproszenie</b> — wtedy oddasz głos.
    </Text>
    <Text color="gray.500" fontSize="14px" mb="22px" lineHeight="1.6" maxW="460px">
      Masz już konto? Zaloguj się tym samym adresem e-mail.
    </Text>
    <HStack spacing="10px" flexWrap="wrap" justify="center">
      <Button as={Link} to="/rejestracja" bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiUserPlus />} _hover={{ bg: "#36007a" }}>
        Załóż konto
      </Button>
      <Button as={Link} to="/logowanie" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" leftIcon={<FiLogIn />} _hover={{ bg: "rgba(67,0,152,0.06)" }}>
        Zaloguj się
      </Button>
    </HStack>
  </Flex>
);

// Informacyjny baner przy elektoracie zamkniętym/„zapisy" — wyjaśnia zasady dostępu.
const AccessNotice: React.FC<{ kind: "closed" | "signup"; signedUp: boolean }> = ({ kind, signedUp }) => {
  const text =
    kind === "closed"
      ? "To głosowanie jest tylko dla zaproszonych. Podaj poniżej adres e-mail, na który otrzymałeś zaproszenie — inne adresy nie są uprawnione."
      : signedUp
      ? "Bierzesz w nim udział jako osoba zapisana. Podaj e-mail, którym się zapisałeś."
      : "To głosowanie jest dla osób, które zapisały się wcześniej. Jeśli się nie zapisałeś, Twój adres nie będzie uprawniony.";
  return (
    <Flex bg="rgba(67,0,152,0.05)" border="1px solid rgba(67,0,152,0.14)" borderRadius="14px" p="14px 16px" gap="11px" align="flex-start">
      <Icon as={kind === "signup" ? FiUserPlus : FiInfo} color="primary" boxSize="18px" mt="1px" flexShrink={0} />
      <Text fontSize="14px" color="gray.600" lineHeight="1.55">
        {text}
      </Text>
    </Flex>
  );
};

// Bramka dla ZALOGOWANYCH, których konto nie jest uprawnione (zamknięty elektorat / brak zapisu).
// Zalogowany głosuje „jako konto" — więc zamiast inputu e-mail (mechanizm dla anonimowych) dostaje jasny komunikat.
const EnrollmentGate: React.FC<{ electorate?: string }> = ({ electorate }) => {
  const isSignup = electorate === "signup";
  return (
    <Flex direction="column" align="center" textAlign="center" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.10)" p={{ base: "34px 22px", md: "44px 36px" }}>
      <Flex w="64px" h="64px" borderRadius="full" bg="rgba(229,31,68,0.10)" align="center" justify="center" mb="16px">
        <Icon as={FiUserX} boxSize="30px" color="#e51f44" />
      </Flex>
      <Heading fontSize="22px" color="primary" fontWeight="800" mb="10px">
        {isSignup ? "Nie jesteś zapisany" : "Brak uprawnień do tego głosowania"}
      </Heading>
      <Text color="gray.600" mb="22px" lineHeight="1.6" maxW="460px">
        {isSignup
          ? "Na tym koncie nie ma zapisu na to głosowanie. Głosować mogą wyłącznie osoby, które zapisały się wcześniej — a zapisy są już zamknięte."
          : "To głosowanie jest tylko dla zaproszonych. Twoje konto nie znajduje się na liście uprawnionych dodanej przez organizatora."}
      </Text>
      <Button as={Link} to="/aktywne-glosowania" variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" _hover={{ bg: "rgba(67,0,152,0.05)" }}>
        Zobacz inne głosowania
      </Button>
    </Flex>
  );
};

interface VotingsViewProps {
  model: VotingViewModel;
}

const VotingView: React.FC<VotingsViewProps> = observer(({ model }) => {
  const { id } = useParams();

  useEffect(() => {
    document.title = "Głosowanie";
    id && model.getVoting(id, toast, navigate);
  }, []);

  const toast = useToast();
  const navigate = useNavigate();
  const [isReportPopupOpen, setReportPopupOpen] = useState(false);


  const sendCommand = useMemo(() => model.handleSend(toast, navigate), [toast, navigate]);

  // Status renderowany DYNAMICZNIE (bez twardych przekierowań — inaczej „wstecz" wpadał w pętlę przeładowań).
  if (!model.isLoading && model.status === VOTING_STATUSES.PLANNED) {
    return <VotingPlannedView model={model} />;
  }
  if (!model.isLoading && model.status === VOTING_STATUSES.FINISHED) {
    return <VotingFinishedScreen name={model.name} endDate={model.endDate} />;
  }
  // Zalogowany bez uprawnień (np. niepubliczne głosowanie spoza jego listy) → czysty komunikat, nie „goły" input e-mail.
  if (!model.isLoading && model.accessDenied) {
    return (
      <AdaptiveShell>
        <Box w="100%" maxW="720px" mx="auto" px={{ base: "16px", md: "20px" }} pt={{ base: "14px", md: "20px" }}>
          <Button onClick={() => navigate(-1)} variant="ghost" size="sm" color="gray.500" leftIcon={<Icon as={FiArrowLeft} />} fontWeight="700" _hover={{ color: "primary" }}>
            Wstecz
          </Button>
        </Box>
        <Box w="100%" maxW="560px" mx="auto" px={{ base: "16px", md: "20px" }} py={{ base: "40px", md: "64px" }}>
          <Flex direction="column" align="center" textAlign="center" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.10)" p={{ base: "36px 22px", md: "48px 36px" }}>
            <Flex w="66px" h="66px" borderRadius="full" bg="rgba(229,31,68,0.10)" align="center" justify="center" mb="18px">
              <Icon as={FiUserX} boxSize="32px" color="#e51f44" />
            </Flex>
            <Heading fontSize="23px" color="primary" fontWeight="800" mb="8px">
              Brak dostępu do tego głosowania
            </Heading>
            <Text color="gray.600" mb="22px" lineHeight="1.6">
              To głosowanie nie jest publiczne, a Twoje konto nie znajduje się na liście osób uprawnionych dodanej przez organizatora.
            </Text>
            <Button as={Link} to="/aktywne-glosowania" bg="primary" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#36007a" }}>
              Odkrywaj inne głosowania
            </Button>
          </Flex>
        </Box>
      </AdaptiveShell>
    );
  }

  const loggedIn = isLoggedIn();
  const notEligible = !model.accountEligible;
  // Bramka weryfikacji: głosowanie wymaga zweryfikowanej tożsamości, a oglądający jej nie ma.
  const verificationGate =
    !model.isOwner && !model.accountVoted && model.requiresVerification && notEligible && !(loggedIn && model.accountVerified);
  // Bramka CyberID: głosowanie wymaga zweryfikowanej tożsamości (per głosowanie), a wyborca jeszcze jej nie potwierdził.
  const cyberidGate = model.verifiedIdentityRequired && !model.cyberidVerified && !model.isOwner && !model.accountVoted;
  // Bramka „tylko z kontami": niezalogowany nie użyje formularza e-mail (głos i tak blokowany serwerowo).
  const accountGate = !model.isOwner && !model.accountVoted && !verificationGate && model.accountRequired && !loggedIn;
  // Bramka uprawnień: ZALOGOWANY, którego konto nie jest na liście (zamknięty) lub nie ma zapisu (signup).
  const enrollmentGate =
    loggedIn &&
    !model.isOwner &&
    !model.accountVoted &&
    !verificationGate &&
    notEligible &&
    (model.electorateType === "closed" || (model.electorateType === "signup" && !model.signedUp));
  // Baner kontekstowy dla elektoratu zamkniętego/„zapisy" — tylko gdy pokazujemy formularz e-mail (anonimowi / zapisani).
  const accessNotice: "closed" | "signup" | null =
    !model.isOwner && !model.accountVoted && !verificationGate && !accountGate && !enrollmentGate && notEligible
      ? model.electorateType === "closed"
        ? "closed"
        : model.electorateType === "signup"
        ? "signup"
        : null
      : null;

  return (
    <AdaptiveShell>
      <WalletHandoff model={model} />
      <Box w="100%" maxW="720px" mx="auto" px={{ base: "16px", md: "20px" }} pt={{ base: "14px", md: "20px" }}>
        <Button onClick={() => navigate(-1)} variant="ghost" size="sm" color="gray.500" leftIcon={<Icon as={FiArrowLeft} />} fontWeight="700" _hover={{ color: "primary" }}>
          Wstecz
        </Button>
      </Box>
      {model.isOwner && id && <OrganizerToolbar id={id} />}
      {model.isLoading ? (
        <CustomLoader />
      ) : model.accountVoted ? (
        <Box w="100%" maxW="560px" mx="auto" px={{ base: "16px", md: "20px" }} py={{ base: "40px", md: "64px" }}>
          <Flex direction="column" align="center" textAlign="center" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.10)" p={{ base: "36px 22px", md: "48px 36px" }}>
            <Flex w="66px" h="66px" borderRadius="full" bg="rgba(47,206,37,0.14)" align="center" justify="center" mb="18px">
              <Icon as={FiCheckCircle} boxSize="34px" color="#1f9e18" />
            </Flex>
            <Heading fontSize="23px" color="primary" fontWeight="800" mb="8px">
              Twój głos został oddany
            </Heading>
            <Text color="gray.500" mb="22px" lineHeight="1.6">
              Dziękujemy! Po zakończeniu głosowania zobaczysz wyniki tutaj.
            </Text>
            <Button as={Link} to="/aktywne-glosowania" bg="primary" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#36007a" }}>
              Odkrywaj inne głosowania
            </Button>
          </Flex>
        </Box>
      ) : (
        <Box w="100%" maxW="720px" mx="auto" px={{ base: "16px", md: "20px" }} py={{ base: "28px", md: "48px" }}>
          <VStack align="stretch" spacing="18px">
            {/* Info o głosowaniu (tytuł, organizator, daty) — widoczne zawsze, też za bramką e-mail */}
            <VotingInfoTile model={model} />

            {id && <OnChainVotes votingId={id} />}

            {id && <BlockchainInfo votingId={id} />}

            {/* Za bramką e-mail: read-only podgląd pytań, żeby wyborca wiedział, na co głosuje, zanim się zidentyfikuje */}
            {model.isEmailNecessary && model.questionsPreview.length > 0 && (
              <VotingPreviewBox preview={{ title: model.name ?? "", secret: !!model.secret, questions: model.questionsPreview }} />
            )}

            {!model.isEmailNecessary &&
              model.questions?.map((question, i) => (
                <VotingViewQuestion question={question} key={question.id} model={model} index={i} />
              ))}

            {/* Oddanie głosu (e-mail) LUB bramka dostępu (weryfikacja / brak uprawnień konta) */}
            {cyberidGate ? (
              <CyberidVerifyGate minAssuranceLevel={model.minAssuranceLevel} onVerify={() => model.startVerification()} />
            ) : verificationGate ? (
              <VerificationGate loggedIn={loggedIn} />
            ) : accountGate ? (
              <AccountRequiredGate />
            ) : enrollmentGate ? (
              <EnrollmentGate electorate={model.electorateType} />
            ) : (
              <>
                {accessNotice && <AccessNotice kind={accessNotice} signedUp={model.signedUp} />}

                {model.cyberidVerified ? (
                  <Flex align="center" gap="10px" bg="rgba(47,206,37,0.10)" border="1px solid rgba(47,206,37,0.25)" borderRadius="12px" p="12px 14px">
                    <Icon as={FiCheckCircle} color="#1f9e18" boxSize="20px" />
                    <Text fontSize="14px" color="gray.700">
                      Tożsamość zweryfikowana przez CyberID — głosujesz jako <b>{model.email}</b>
                    </Text>
                  </Flex>
                ) : (
                  <VotingViewEmail model={model} />
                )}

                <CustomButton text={model.isEmailNecessary ? "Przejdź do karty do głosowania" : "Oddaj głos"} {...bindToCommand(sendCommand)} w="100%" mt="6px" />
                <Text fontSize="13px" color="gray.500" textAlign="center">
                  {model.isEmailNecessary
                    ? "Wpisz e-mail, na który dostałeś zaproszenie — pokażemy Twoją kartę do głosowania. Głosu jeszcze nie oddajesz."
                    : model.accountEligible && model.voteConfirmation === "in_session"
                    ? "Twój głos zostanie zapisany od razu — bez potwierdzenia mailem."
                    : model.voteConfirmation === "app"
                    ? "Głos potwierdzisz w aplikacji (do czasu jej premiery — linkiem z e-maila)."
                    : "Po wysłaniu potwierdzisz swój głos linkiem z wiadomości e-mail."}
                </Text>
              </>
            )}

            {loggedIn && (
              <Flex justify="center" pt="4px">
                <Button variant="ghost" size="sm" color="gray.400" fontWeight="600" onClick={() => setReportPopupOpen(true)} _hover={{ color: "accent", bg: "rgba(229,31,68,0.05)" }}>
                  Zgłoś głosowanie
                </Button>
              </Flex>
            )}
            {loggedIn && isReportPopupOpen && <ReportPopup Id={id as string} onClose={() => setReportPopupOpen(false)} reportableType="voting" model={model} />}
          </VStack>
        </Box>
      )}
    </AdaptiveShell>
  );
});

const VotingViewWithModel = withModel<VotingViewModel>(VotingView, VotingViewModel);

export default VotingViewWithModel;
